/**
 * NotesList Component
 * Paginated list of notes using FlatList with infinite scrolling.
 * Supports optional filtering by business or menu item.
 */

import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback, useMemo } from 'react';
import { ActivityIndicator, FlatList, Pressable, Text, View } from 'react-native';

import { useAppTheme } from '@/providers/theme-provider';
import { NoteWire } from '@/api/schemas/note';

import { useMyNotes } from '../hooks/use-notes';
import { NoteCard } from './note-card';

export interface NotesListProps {
  /** User ID to fetch notes for (currently unused; API returns current user's notes) */
  userId: string;
  /** Optional business ID filter */
  businessId?: string;
  /** Optional menu item ID filter */
  menuItemId?: string;
  /** Callback when a note is saved after inline editing */
  onSave: (noteId: string, content: string) => void;
  /** Callback when delete is pressed on a note */
  onDelete: (note: NoteWire) => void;
  /** When true, renders as plain View instead of FlatList (for use inside ScrollView) */
  nested?: boolean;
}

export function NotesList({
  userId: _userId,
  businessId,
  menuItemId,
  onSave,
  onDelete,
  nested = false,
}: NotesListProps) {
  const theme = useAppTheme();

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (businessId) p.business_id = businessId;
    if (menuItemId) p.menu_item_id = menuItemId;
    return p;
  }, [businessId, menuItemId]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useMyNotes(params);

  const allNotes = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.items);
  }, [data]);

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderItem = useCallback(
    ({ item }: { item: NoteWire }) => (
      <NoteCard note={item} onSave={onSave} onDelete={onDelete} />
    ),
    [onSave, onDelete]
  );

  const keyExtractor = useCallback((item: NoteWire) => item.id, []);

  const renderFooter = useCallback(() => {
    if (!isFetchingNextPage) return null;
    return (
      <View className="py-4 items-center">
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }, [isFetchingNextPage, theme.colors.primary]);

  // Loading state
  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center py-12">
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  // Empty state
  if (allNotes.length === 0) {
    return (
      <View className="flex-1 items-center justify-center py-12 px-6">
        <Ionicons
          name="document-text-outline"
          size={48}
          className="text-muted-foreground"
        />
        <Text className="text-base font-medium mt-4 text-center text-muted-foreground">
          No tasting notes yet
        </Text>
        <Text className="text-sm mt-2 text-center text-muted-foreground/70">
          Start adding notes to remember flavors, textures, and what to try next time.
        </Text>
      </View>
    );
  }

  if (nested) {
    return (
      <View style={{ paddingHorizontal: 16, paddingTop: 8 }}>
        {allNotes.map((item) => (
          <NoteCard key={item.id} note={item} onSave={onSave} onDelete={onDelete} />
        ))}
        {hasNextPage && (
          <Pressable
            onPress={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="items-center py-4"
          >
            {isFetchingNextPage ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Text className="text-sm font-medium text-primary">Load more</Text>
            )}
          </Pressable>
        )}
      </View>
    );
  }

  return (
    <FlatList
      data={allNotes}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.5}
      ListFooterComponent={renderFooter}
      contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 8 }}
      showsVerticalScrollIndicator={false}
    />
  );
}

export default NotesList;
